
(() => {
  'use strict';

  /* =========================================
     CAFÉ COMETS RADIO
     Header player
     ========================================= */

  const RADIO_API = 'https://radio.cafecomets.fr/radio.php';
  const PLAYING_KEY = 'cafeCometsRadioPlaying';

  /* =========================================
     ELEMENTS
     ========================================= */

  const radioBar = document.getElementById('cafe-comets-radio');
  const audio = document.getElementById('cc-radio-audio');
  const toggle = document.getElementById('cc-radio-toggle');
  const trackDisplay = document.getElementById('cc-radio-track');

  if (!radioBar || !audio || !toggle || !trackDisplay) {
    return;
  }

  const buttonLabel = toggle.querySelector('.cc-radio__button-label');

  let radioLoaded = false;
  let tunePromise = null;
  let retryTimer = null;
  let playRequested = false;

  /* =========================================
     BUTTON STATE
     ========================================= */

  function setButtonPlaying() {
    toggle.classList.add('is-playing');

    if (buttonLabel) {
      buttonLabel.textContent = 'PAUSE';
    }

    toggle.setAttribute('aria-label', 'Pause Café Comets Radio');
    toggle.setAttribute('aria-pressed', 'true');
  }

  function setButtonPaused() {
    toggle.classList.remove('is-playing');

    if (buttonLabel) {
      buttonLabel.textContent = 'LISTEN';
    }

    toggle.setAttribute('aria-label', 'Écouter Café Comets Radio');
    toggle.setAttribute('aria-pressed', 'false');
  }

  function setTrackTitle(track) {
    if (!track || !track.artist || !track.title) {
      trackDisplay.textContent = 'Radio unavailable';
      return;
    }

    trackDisplay.textContent = `${track.artist} — ${track.title}`;
  }

  /* =========================================
     RADIO API
     ========================================= */

  async function getNowPlaying() {
    const response = await fetch(`${RADIO_API}?t=${Date.now()}`, {
      cache: 'no-store'
    });

    if (!response.ok) {
      throw new Error(`Radio API: ${response.status}`);
    }

    const data = await response.json();

    if (!data.ok || !data.now_playing) {
      throw new Error('Réponse radio invalide');
    }

    return data.now_playing;
  }

  async function displayNowPlaying() {
    try {
      const track = await getNowPlaying();
      setTrackTitle(track);
    } catch (error) {
      console.error('Café Comets Radio:', error);
      trackDisplay.textContent = 'Radio unavailable';
    }
  }

  /* =========================================
     LOAD AUDIO METADATA
     ========================================= */

  function loadAudioMetadata() {
    return new Promise((resolve, reject) => {
      const cleanup = () => {
        audio.removeEventListener('loadedmetadata', onReady);
        audio.removeEventListener('error', onError);
      };

      const onReady = () => {
        cleanup();
        resolve();
      };

      const onError = () => {
        cleanup();
        reject(new Error('Impossible de charger le flux audio'));
      };

      audio.addEventListener('loadedmetadata', onReady, { once: true });
      audio.addEventListener('error', onError, { once: true });

      audio.load();
    });
  }

  /* =========================================
     TUNE TO THE LIVE POSITION
     ========================================= */

  async function tuneToLive() {
    if (tunePromise) {
      return tunePromise;
    }

    tunePromise = (async () => {
      const track = await getNowPlaying();

      setTrackTitle(track);

      if (!track.url) {
        throw new Error('URL du flux audio manquante');
      }

      audio.src = track.url;

      await loadAudioMetadata();

      const startedAt = Number(track.started_at) || 0;
      const serverPosition = Number(track.position) || 0;
      const now = Date.now() / 1000;

      let livePosition = serverPosition;

      if (startedAt > 0) {
        livePosition = now - startedAt;
      }

      if (
        Number.isFinite(audio.duration) &&
        audio.duration > 0 &&
        livePosition >= audio.duration
      ) {
        throw new Error('Le morceau a changé pendant le chargement');
      }

      audio.currentTime = Math.max(0, livePosition);
      radioLoaded = true;

      return track;
    })();

    try {
      return await tunePromise;
    } finally {
      tunePromise = null;
    }
  }

  /* =========================================
     PLAY
     ========================================= */

  async function playRadio() {
    if (playRequested) {
      return;
    }

    playRequested = true;
    toggle.disabled = true;

    try {
      await tuneToLive();
      await audio.play();

      sessionStorage.setItem(PLAYING_KEY, '1');
      setButtonPlaying();
    } catch (error) {
      console.error('Café Comets Radio:', error);

      sessionStorage.setItem(PLAYING_KEY, '0');
      setButtonPaused();
    } finally {
      toggle.disabled = false;
      playRequested = false;
    }
  }

  /* =========================================
     PAUSE
     ========================================= */

  function pauseRadio() {
    audio.pause();

    sessionStorage.setItem(PLAYING_KEY, '0');
    setButtonPaused();
  }

  /* =========================================
     PLAY / PAUSE CONTROL
     ========================================= */

  toggle.addEventListener('click', () => {
    if (audio.paused) {
      playRadio();
    } else {
      pauseRadio();
    }
  });

  /* =========================================
     AUDIO EVENTS
     ========================================= */

  audio.addEventListener('play', setButtonPlaying);
  audio.addEventListener('pause', setButtonPaused);

  audio.addEventListener('ended', async () => {
    if (sessionStorage.getItem(PLAYING_KEY) !== '1') {
      return;
    }

    try {
      await tuneToLive();
      await audio.play();
      setButtonPlaying();
    } catch (error) {
      console.error('Café Comets Radio:', error);
      setButtonPaused();
      scheduleReconnect();
    }
  });

  audio.addEventListener('error', () => {
    if (sessionStorage.getItem(PLAYING_KEY) === '1') {
      scheduleReconnect();
    }
  });

  /* =========================================
     RECONNECT
     ========================================= */

  function scheduleReconnect() {
    if (retryTimer) {
      return;
    }

    retryTimer = window.setTimeout(async () => {
      retryTimer = null;

      if (sessionStorage.getItem(PLAYING_KEY) !== '1') {
        return;
      }

      try {
        await tuneToLive();
        await audio.play();
        setButtonPlaying();
      } catch (error) {
        console.error('Café Comets Radio — reconnexion:', error);
        setButtonPaused();

        if (sessionStorage.getItem(PLAYING_KEY) === '1') {
          scheduleReconnect();
        }
      }
    }, 3000);
  }

  /* =========================================
     INITIALISATION
     ========================================= */

  setButtonPaused();
  displayNowPlaying();

  /*
   * Après un changement de page, on tente de reprendre
   * la lecture si elle était active. Le navigateur peut
   * bloquer cette reprise automatique.
   */

  if (sessionStorage.getItem(PLAYING_KEY) === '1') {
    playRadio();
  }
})();
